import styled from "styled-components";
import React from "react";
import { Link, usePage } from "@inertiajs/react";

import {
  LandingLanguageProvider,
  useLandingLanguage,
} from "../../../contexts/landing-language";
import { propertyHome, roomPath } from "../../../components/insights/paths";
import StrapiRichText from "../../../components/StrapiRichText";
import Header from "../home/Header";
import Footer from "../home/Footer";

const COPY = {
  en: {
    from: "From",
    perNight: "per night",
    disclaimer:
      "Indicative rate. Final price depends on dates and availability, and is confirmed at booking.",
    size: "Room size",
    count: "Rooms of this type",
    book: "See booking options",
    others: "Other rooms",
  },
  id: {
    from: "Mulai dari",
    perNight: "per malam",
    disclaimer:
      "Harga indikatif. Harga akhir tergantung tanggal dan ketersediaan, dan dikonfirmasi saat pemesanan.",
    size: "Luas kamar",
    count: "Jumlah kamar tipe ini",
    book: "Lihat pilihan pemesanan",
    others: "Kamar lainnya",
  },
};

const rupiah = (value) => `Rp ${Number(value || 0).toLocaleString("id-ID")}`;

const Page = styled.main`
  background: #fff;
  padding: 140px 24px 80px;
`;

const Container = styled.div`
  margin: 0 auto;
  max-width: 980px;
`;

const Title = styled.h1`
  font-size: clamp(28px, 4vw, 40px);
  font-weight: 700;
  margin-bottom: 10px;
`;

const Lede = styled.p`
  color: #57513f;
  font-size: 17px;
  line-height: 1.75;
  margin-bottom: 28px;
`;

const Price = styled.p`
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 6px;
`;

const PriceNote = styled.p`
  color: #5e6278;
  font-size: 13px;
  line-height: 1.6;
  margin-bottom: 28px;
`;

const Facts = styled.dl`
  border-top: 1px solid #ece8e0;
  display: grid;
  gap: 0 24px;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  margin-bottom: 36px;
  padding-top: 20px;

  dt {
    color: #78725f;
    font-size: 13px;
    margin-bottom: 4px;
  }

  dd {
    font-size: 17px;
    font-weight: 600;
    margin: 0 0 16px;
  }
`;

const Photos = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  margin-bottom: 36px;
`;

const Photo = styled.img`
  aspect-ratio: 4 / 3;
  border-radius: 12px;
  display: block;
  object-fit: cover;
  width: 100%;
`;

const Body = styled.div`
  color: #4a4535;
  font-size: 16px;
  line-height: 1.85;
  margin-bottom: 40px;
`;

const Cta = styled.a`
  background: #1a1a1a;
  border-radius: 999px;
  color: #fff;
  display: inline-block;
  font-weight: 600;
  padding: 14px 28px;
`;

const Others = styled.nav`
  border-top: 1px solid #ece8e0;
  margin-top: 56px;
  padding-top: 28px;
`;

const OthersTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 16px;
`;

const OtherLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
`;

const OtherLink = styled(Link)`
  border: 1px solid #ded8cc;
  border-radius: 999px;
  color: #4a4535;
  font-size: 15px;
  padding: 8px 18px;

  &:hover {
    border-color: #c9bfa8;
    color: #1a1a1a;
  }
`;

const RoomContent = (props) => {
  const { room, other_rooms: otherRooms } = usePage().props;
  const { language } = useLandingLanguage();
  const copy = COPY[language] || COPY.en;

  return (
    <Page>
      <Container>
        <Title>{room.name}</Title>
        {room.short_desc ? <Lede>{room.short_desc}</Lede> : null}

        {room.price_from ? (
          <React.Fragment>
            <Price>
              {copy.from} {rupiah(room.price_from)} {copy.perNight}
            </Price>
            <PriceNote>{copy.disclaimer}</PriceNote>
          </React.Fragment>
        ) : null}

        <Facts>
          {room.luas_m2 ? (
            <div>
              <dt>{copy.size}</dt>
              <dd>{room.luas_m2} m²</dd>
            </div>
          ) : null}
          {room.number_of_rooms ? (
            <div>
              <dt>{copy.count}</dt>
              <dd>{room.number_of_rooms}</dd>
            </div>
          ) : null}
        </Facts>

        {room.image_urls?.length ? (
          <Photos>
            {room.image_urls.map((url, index) => (
              <Photo
                key={url}
                src={url}
                alt={`${room.name} — Hebe Hostel Bogor`}
                loading={index === 0 ? "eager" : "lazy"}
                fetchPriority={index === 0 ? "high" : undefined}
              />
            ))}
          </Photos>
        ) : null}

        {room.long_desc ? (
          <Body>
            <StrapiRichText content={room.long_desc} />
          </Body>
        ) : null}

        <Cta href={`${propertyHome(language)}#booking`}>{copy.book}</Cta>

        {otherRooms?.length ? (
          <Others>
            <OthersTitle>{copy.others}</OthersTitle>
            <OtherLinks>
              {otherRooms.map((other) => (
                <OtherLink
                  key={other.slug}
                  href={roomPath(language, other.slug)}
                >
                  {other.name}
                </OtherLink>
              ))}
            </OtherLinks>
          </Others>
        ) : null}
      </Container>
    </Page>
  );
};

const RoomTypeShow = (props) => {
  return (
    <LandingLanguageProvider>
      <React.Fragment>
        <Header forceScrolled />
        <RoomContent />
        <Footer />
      </React.Fragment>
    </LandingLanguageProvider>
  );
};

export default RoomTypeShow;
